import Toast from 'react-native-toast-message'
import * as Sentry from '@sentry/react-native';
import { getCurrentOrgID } from './organization';
import ErrorHandler from '../utils/ErrorHandler';
import { supabase } from '../data/client';
import findAssociativeChanges from '../utils/findAssociativeChanges';
import * as Crypto from 'expo-crypto';
import { createNotification } from './notifications';
import { queryClient } from '../data/client';
import { PEOPLE_SELECT } from '../utils/peopleSelect';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import useAuthStore from '../data/store/useAuthStore';
import { saveFile } from '../utils/FileManager';


const getConversations = async (personID) => {
    const orgID = await getCurrentOrgID();

    //Fetch person-based conversations (PersonConversations)
    const {error, data: personConversations} = await supabase.from('Conversations')
    .select('*, PersonConversations!inner(*), People!PersonConversations(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), Messages(*, Sender:People!senderID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))')
    .eq('organizationID', orgID)
    .eq('PersonConversations.personID', personID)
    .eq('PersonConversations.isDeleted', false)
    .order('sentAt', {referencedTable: 'Messages', ascending: false})
    .limit(1, {referencedTable: 'Messages'});

    if(error){
        ErrorHandler(Error(error?.message), {query: "getConversations", input: {personID, error}})
        return [];
    }

    //Fetch role-based conversations where the user belongs to one of the linked roles
    const {error: roleError, data: roleConversations} = await supabase.from('Conversations')
    .select('*, RoleConversations!inner(roleID), Messages(*, Sender:People!senderID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))')
    .eq('organizationID', orgID)
    .eq('membersByGroup', true)
    .order('sentAt', {referencedTable: 'Messages', ascending: false})
    .limit(1, {referencedTable: 'Messages'});

    if(roleError){
        ErrorHandler(Error(roleError?.message), {query: "getConversations (role-based)", input: {personID, error: roleError}})
    }

    //Filter role conversations to those where the user is in one of the linked roles
    let visibleRoleConversations = [];
    if(roleConversations && roleConversations.length > 0){
        const roleIDs = [...new Set(roleConversations.flatMap(c => c.RoleConversations?.map(rc => rc.roleID) || []))];
        if(roleIDs.length > 0){
            const {data: rolePeople} = await supabase.from('RolePeople')
                .select('roleID, personID')
                .in('roleID', roleIDs)
                .eq('personID', personID);

            const userRoleIDs = new Set(rolePeople?.map(rp => rp.roleID) || []);
            visibleRoleConversations = roleConversations.filter(c =>
                c.RoleConversations?.some(rc => userRoleIDs.has(rc.roleID))
            );
        }
    }

    //Merge both lists
    const allConversations = [...(personConversations || []), ...visibleRoleConversations];

    return allConversations.map(c => {
        const pc = c.PersonConversations?.find(p => p.personID == personID);
        return {...c, lastMessage: c.Messages[0], pinnedForUser: pc?.isPinned, mutedForUser: pc?.isMuted};
    }).sort((a, b) => {
        //Conversations with messages sort by lastMessage.sentAt desc (newest first).
        //Conversations with no messages sink to the bottom, ordered by createdAt desc
        //(newest empty conversations first). Avoids NaN from new Date(undefined) which
        //made .sort() scatter no-message conversations unpredictably.
        const aHas = !!a.lastMessage?.sentAt;
        const bHas = !!b.lastMessage?.sentAt;
        if(aHas && bHas) return new Date(b.lastMessage.sentAt) - new Date(a.lastMessage.sentAt);
        if(aHas) return -1;
        if(bHas) return 1;
        return new Date(b.createdAt) - new Date(a.createdAt);
    })
}


const getConversation = async (conversationID) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('Conversations').select(`*, People:People!PersonConversations(${PEOPLE_SELECT}), pc:PersonConversations(*)`).eq('organizationID', orgID).eq('id', conversationID).maybeSingle();

    if(error){
        ErrorHandler(Error(error?.message), {query: "getConversation", input: {organizationID: orgID, ...(error)}})
        return [];
    }

    if(data){
        if(data.membersByGroup){
            //Role-based: fetch roles via RoleConversations, then resolve People from RolePeople
            const {data: roleConvData, error: roleConvError} = await supabase.from('RoleConversations')
                .select('Role:Roles(*)')
                .eq('conversationID', conversationID)
                .eq('organizationID', orgID);

            if(roleConvError){
                ErrorHandler(Error(roleConvError?.message), {query: "getConversation (roles)", input: {conversationID, organizationID: orgID, ...roleConvError}})
            }
            data.Roles = !roleConvError ? (roleConvData?.map(rc => rc.Role).filter(Boolean) || []) : [];

            const roleIDs = data.Roles?.map(r => r.id) || [];
            if(roleIDs.length > 0){
                const {data: rolePeople, error: rpError} = await supabase.from('RolePeople')
                    .select(`Person:People(${PEOPLE_SELECT})`)
                    .in('roleID', roleIDs)
                    .eq('organizationID', orgID);

                if(rpError){
                    ErrorHandler(Error(rpError?.message), {query: "getConversation (role people)", input: {conversationID, organizationID: orgID, roleIDs, ...rpError}})
                }
                if(!rpError && rolePeople){
                    //Deduplicate by person ID (a person may be in multiple roles)
                    const seen = new Set();
                    data.People = rolePeople
                        .map(rp => rp.Person)
                        .filter(p => {
                            if(!p || seen.has(p.id)) return false;
                            seen.add(p.id);
                            return true;
                        });
                } else {
                    data.People = [];
                }
            } else {
                data.People = [];
            }
        } else {
            data.People = data.People?.map(p => {
                const pc = data.pc?.find(entry => entry.personID == p.id);
                return {...p, isMuted: pc?.isMuted, isAdmin: pc?.isAdmin};
            });
        }
        delete data.pc;
    }

    return data
}


const getAllOrgConversations = async () => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('Conversations')
        .select('*, PersonConversations(personID, isDeleted), People!PersonConversations(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), Messages(*, Sender:People!senderID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))')
        .eq('organizationID', orgID)
        .order('sentAt', {referencedTable: 'Messages', ascending: false})
        .limit(1, {referencedTable: 'Messages'});

    if(error){
        ErrorHandler(Error(error?.message), {query: "getAllOrgConversations", input: {error}})
        return [];
    }

    return data?.map(c => ({
        ...c,
        lastMessage: c.Messages?.[0],
    })).sort((a, b) => new Date(b.lastMessage?.sentAt) - new Date(a.lastMessage?.sentAt)) || [];
}


const getBoards = async () => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.rpc('boards_ordered_by_last_post', {organizationid: orgID});

    if(error){
        ErrorHandler(Error(error?.message), {query: "getBoards", input: {organizationID: orgID, ...error}}, `Something went wrong fetching the boards`)
        return [];
    }

    if(!data || data.length == 0){
        return [];
    }

    const {error: roleError, data: roleData} = await supabase.from('BoardRoles').select('*, Role:Roles(*)').eq('organizationID', orgID).in('boardID', data.map(b => b.id));

    if(roleError){
        ErrorHandler(Error(roleError.message), {query: "getBoardRoles", input: {organizationID: orgID, boardIDs: data.map(b => b.id)}, ...roleError}, `Something went wrong fetching the roles for the boards`)
    }

    const rolesByBoardID = {};
    roleData?.forEach(r => {
        if(!rolesByBoardID[r.boardID]) rolesByBoardID[r.boardID] = [];
        rolesByBoardID[r.boardID].push(r);
    });

    //Merge current user's per-board mute/pin/hide state (PersonBoards)
    let muteMap = new Map(), pinMap = new Map(), hideMap = new Map();
    try {
        const states = await getMyBoardStates(data.map(b => b.id));
        muteMap = states.muteMap; pinMap = states.pinMap; hideMap = states.hideMap;
    } catch { /* server-side gate is the safety net */ }

    return data.map(b => ({
        ...b,
        avatarPath: b.avatarPath ?? b.avatarpath ?? null,
        lastPostDate: b.most_recent_post_date ?? b.mostRecentPostDate ?? null,
        isArchived: b.isArchived ?? b.isarchived ?? false,
        Roles: rolesByBoardID[b.id] || [],
        mutedForUser: muteMap.get(b.id) ?? false,
        pinnedForUser: pinMap.get(b.id) ?? false,
        hiddenForUser: hideMap.get(b.id) ?? false
    }))
}


const getAllOrgBoards = async () => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('Boards')
        .select('*')
        .eq('organizationID', orgID)
        .eq('type', 'MESSAGE_BOARD')
        .order('name', {ascending: true});

    if(error){
        ErrorHandler(Error(error?.message), {query: "getAllOrgBoards", input: {organizationID: orgID, ...error}})
        return [];
    }

    if(!data || data.length == 0){
        return [];
    }

    const {error: roleError, data: roleData} = await supabase.from('BoardRoles').select('*, Role:Roles(*)').eq('organizationID', orgID).in('boardID', data.map(b => b.id));

    if(roleError){
        ErrorHandler(Error(roleError.message), {query: "getAllOrgBoards_roles", input: {organizationID: orgID, boardIDs: data.map(b => b.id)}, ...roleError})
        return data;
    }

    const rolesByBoardID = {};
    roleData?.forEach(r => {
        if(!rolesByBoardID[r.boardID]) rolesByBoardID[r.boardID] = [];
        rolesByBoardID[r.boardID].push(r);
    });

    return data.map(b => ({...b, Roles: rolesByBoardID[b.id] || []}))
}


const getBoardPermissions = (board, loggedInUser) => {
    const currentUserRoleIDs = loggedInUser?.Roles?.map(role => role.id) || [];
    const permissions = {canPost: false, canComment: false, canReact: false, isAdmin: false};
    if(board?.type === 'ANNOUNCEMENTS'){
        const settings = board.AnnouncementSettings;
        const canSend = !!loggedInUser?.can?.('Send SMS Messages');
        return {canPost: canSend, isAdmin: canSend, canComment: !!loggedInUser && settings?.allowComments === true, canReact: !!loggedInUser && settings?.allowReactions === true};
    }

    if(loggedInUser?.id == board?.createdByID){
        permissions.canPost = true
        permissions.canComment = true
        permissions.canReact = true
        permissions.isAdmin = true
    }
    else{
        board?.Roles?.forEach(r => {
            if(currentUserRoleIDs?.indexOf(r.roleID) > -1){
                // Board Admin grants all permissions
                if(r.isAdmin){
                    permissions.canPost = true
                    permissions.canComment = true
                    permissions.canReact = true
                    permissions.isAdmin = true
                }
                else{
                    permissions.canPost = r.canPost ? true : permissions.canPost
                    permissions.canComment = r.canComment ? true : permissions.canComment
                    permissions.canReact = r.canReact ? true : permissions.canReact
                }
            }
        })
    }

    return permissions;
}

const getBoard = async (boardID) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('Boards').select(`*,
        Roles:BoardRoles(*, Role:Roles(*))
    `).eq('organizationID', orgID).eq('id', boardID).maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getBoard", input: {organizationID: orgID, boardID: boardID}, ...error}, `Something went wrong fetching the board`)
        return [];
    }

    // Board not found (deleted or stale link) — don't report to Sentry. See CHURCH-APP-15K.
    if(!data){
        return null;
    }

    const loggedInUser = queryClient.getQueryData(QueryKeys.people.loggedInUser());
    if(data.type === 'ANNOUNCEMENTS'){
        const {data: settings, error: settingsError} = await supabase.from('OrganizationAnnouncementSettings').select('allowComments, allowReactions').eq('boardID', boardID).maybeSingle();
        if(settingsError) throw new Error(settingsError.message);
        data.AnnouncementSettings = settings;
    }

    //Fetch current user's per-board mute/pin/hide state (PersonBoards)
    let mutedForUser = false, pinnedForUser = false, hiddenForUser = false;
    try {
        const states = await getMyBoardStates([boardID]);
        mutedForUser = states.muteMap.get(boardID) ?? false;
        pinnedForUser = states.pinMap.get(boardID) ?? false;
        hiddenForUser = states.hideMap.get(boardID) ?? false;
    } catch { /* server-side gate is the safety net */ }

    return {...data, isArchived: data.isArchived ?? data.isarchived ?? false, permissions: getBoardPermissions(data, loggedInUser), mutedForUser, pinnedForUser, hiddenForUser}
}


const attachAnnouncementSettings = async (posts) => {
    const boardIDs = [...new Set(posts.filter(post => post?.Board?.type === 'ANNOUNCEMENTS').map(post => post.Board.id))];
    if(!boardIDs.length) return posts;
    const {data, error} = await supabase.from('OrganizationAnnouncementSettings').select('boardID, allowComments, allowReactions').in('boardID', boardIDs);
    if(error) throw new Error(error.message);
    const settings = new Map(data.map(item => [item.boardID, item]));
    return posts.map(post => post?.Board?.type === 'ANNOUNCEMENTS' ? {...post, Board: {...post.Board, AnnouncementSettings: settings.get(post.Board.id)}} : post);
};

const POSTS_PAGE_SIZE = 20;
const MY_BOARDS_FEED_PAGE_SIZE = 10;

const getMyBoardsFeed = async (boardIDs, pageParam = 0) => {
    if(!boardIDs || boardIDs.length === 0){
        return {data: [], nextPage: undefined};
    }

    //Posts SELECT RLS does not block archived boards — !inner + eq is the
    //server-side gate so stale board lists can't leak archived posts.
    const {error, data} = await supabase.from('Posts').select(`*,
        Board:Boards!inner(id, name, type, createdByID, isArchived, Roles:BoardRoles(*, Role:Roles(*))),
        author:People!authorID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        Reactions:PostReactions(*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)),
        Comments(count)
    `)
    .in('boardID', boardIDs)
    .eq('Board.isArchived', false)
    .order('isPinned', {ascending: false})
    .order('postedAt', {ascending: false})
    .range(pageParam * MY_BOARDS_FEED_PAGE_SIZE, (pageParam * MY_BOARDS_FEED_PAGE_SIZE) + MY_BOARDS_FEED_PAGE_SIZE - 1);

    if(error){
        ErrorHandler(Error(error.message), {query: "getMyBoardsFeed", input: {boardIDs, pageParam}, ...error}, `Something went wrong fetching the boards feed`);
        return {data: [], nextPage: undefined};
    }

    return {data: await attachAnnouncementSettings(data), nextPage: data.length < MY_BOARDS_FEED_PAGE_SIZE ? undefined : pageParam + 1};
}

const getPostsForBoard = async (boardID, pageParam = 0) => {
    const {error, data} = await supabase.from('Posts').select(`*,
        Board:Boards(id, name, type, createdByID, isArchived, Roles:BoardRoles(*, Role:Roles(*))),
        author:People!authorID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), 
        Reactions:PostReactions(*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), 
        Comments(count)
    `)
    .eq('boardID', boardID)
    .order('isPinned', {ascending: false})
    .order('postedAt', {ascending: false})
    .range(pageParam * POSTS_PAGE_SIZE, (pageParam * POSTS_PAGE_SIZE) + POSTS_PAGE_SIZE - 1);

    if(error){
        ErrorHandler(Error(error.message), {query: "getPostsForBoard", input: {boardID, pageParam}, ...error}, `Something went wrong fetching the posts`)
        return {data: [], nextPage: undefined};
    }

    return {data: await attachAnnouncementSettings(data), nextPage: data.length < POSTS_PAGE_SIZE ? undefined : pageParam + 1};
}


const getPost = async (postID) => {
    const {error, data} = await supabase.from('Posts').select(`*,
        Board:Boards(id, name, type, createdByID, isArchived, Roles:BoardRoles(*, Role:Roles(*))),
        author:People!authorID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        Reactions:PostReactions(*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)),
        Comments(count)
    `).eq('id', postID).maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getPost", input: postID, ...error}, `Something went wrong fetching that post`)
        return {};
    }

    return data ? (await attachAnnouncementSettings([data]))[0] : null
}


//Session-level dedup so feeds/opening a post don't spam upserts. The
//PostViews(postID, personID) PK is the permanent gate — repeat views across
//sessions are a single ignored upsert.
const viewedPostIDs = new Set();

const recordPostView = async ({postID}) => {
    // Skip while emulating — views from an emulation session would be
    // attributed to the wrong person.
    if(useAuthStore.getState().isEmulating) return;
    if(!postID || viewedPostIDs.has(postID)) return;
    viewedPostIDs.add(postID);

    // personID must equal person_id() (the JWT app_metadata.personID claim)
    // or the PostViews INSERT policy rejects it — a stale cached profile
    // (person merge, org switch) violates RLS. Read the claim directly.
    const {data: sessionData} = await supabase.auth.getSession();
    const personID = sessionData?.session?.user?.app_metadata?.personID
        || queryClient.getQueryData(QueryKeys.people.loggedInUser())?.id;

    const orgID = await getCurrentOrgID();
    if(!orgID || !personID) return;

    const {error} = await supabase.from('PostViews')
        .upsert({organizationID: orgID, postID, personID}, {onConflict: 'postID,personID', ignoreDuplicates: true});

    if(error){
        viewedPostIDs.delete(postID);
        // Warn-only: view tracking is invisible bookkeeping — the generic
        // ErrorHandler would scare users with a "no permission" toast even
        // though nothing they did failed (e.g. RLS on edge identity states).
        Sentry.logger.warn('recordPostView failed', {input: {postID}, error: error.message, code: error.code, details: error.details});
    }
}

//RLS limits this to the post author and board admins — everyone else gets [].
const getPostViewers = async (postID) => {
    const {error, data} = await supabase.from('PostViews')
        .select('viewedAt, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
        .eq('postID', postID)
        .order('viewedAt', {ascending: false});

    if(error){
        ErrorHandler(Error(error.message), {query: "getPostViewers", input: {postID}, ...error});
        return [];
    }

    return data;
}


const COMMENTS_PAGE_SIZE = 30;

const getCommentsForPost = async (postID, pageParam = 0) => {
    const {error, data} = await supabase.from('Comments').select(`*, 
        author:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        Reactions:CommentReactions(*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))
    `)
    .eq('postID', postID)
    .order('commentedAt', {ascending: false})
    .range(pageParam * COMMENTS_PAGE_SIZE, (pageParam * COMMENTS_PAGE_SIZE) + COMMENTS_PAGE_SIZE - 1);

    if(error){
        ErrorHandler(Error(error.message), {query: "getCommentsForPost", input: {postID, pageParam}, ...error}, `Something went wrong fetching the comments`)
        return {data: [], nextPage: undefined};
    }

    return {data: data, nextPage: data.length < COMMENTS_PAGE_SIZE ? undefined : pageParam + 1};
}


const savePost = async ({details, boardID, authorID}) => {
    const orgID = await getCurrentOrgID();

    // Scheduling: scheduledFor set → Post Later (isPublished=false, postedAt=future).
    // Otherwise Post Now (isPublished=true, postedAt=now).
    const isScheduled = !!details.scheduledFor;
    const postedAt = isScheduled ? new Date(details.scheduledFor) : new Date();
    const isPublished = details.isPublished !== undefined ? details.isPublished : !isScheduled;

    const newPost = {
        organizationID: orgID,
        boardID: boardID,
        title: details.title,
        body: details.body || "",
        attachments: [...new Set(details.attachments)],
        hidden: false,
        authorID: authorID,
        postedAt: postedAt,
        isPublished: isPublished
    }

    if(!details.id){
        return createPost(newPost)
    }
    else{
        newPost.id = details.id
        return updatePost(newPost)
    }
}


const createPost = async (newPost) => {
    const {error, data} = await supabase.from('Posts').insert(newPost).select(`*,
        Board:Boards(id, name, type, createdByID, isArchived, Roles:BoardRoles(*, Role:Roles(*))),
        author:People!authorID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)
    `).single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createPost", input: newPost, ...error}, `Something went wrong creating that post`);
        return {};
    }

    // Scheduled posts (isPublished=false) get notifications from the backend cron
    // at publish time — skip client-side notifications here.
    if(newPost.isPublished !== false){
        const roleIDs = data.Board.Roles?.map(r => r.Role?.id).filter(Boolean) || [];
        let recipients = [];

        if(roleIDs.length > 0){
            const {data: rolePeople} = await supabase.from('RolePeople')
                .select('personID')
                .eq('organizationID', newPost.organizationID)
                .in('roleID', roleIDs);
            recipients = rolePeople?.map(rp => rp.personID) || [];
        }

        recipients.push(data.Board?.createdByID);

        const uniqueRecipients = [...new Set(recipients)]
        const hasAttachments = data.attachments?.length > 0;

        //Client-side filter: exclude users who muted this board (server-side gate is the safety net)
        let mutedPersonIDs = [];
        try { mutedPersonIDs = await getMutedPersonIDsForBoard(data.Board.id); } catch { /* server-side gate is the safety net */ }
        const mutedSet = new Set(mutedPersonIDs);

        const posterName = `${data.author.firstName} ${data.author.lastName}`;
        const notificationDetails = uniqueRecipients
            .filter(r => r != data.author.id && !mutedSet.has(r))
            .map(r => ({
                type: "NEW_POST",
                path: `connect/boards/${data.Board?.id}/feed`,
                targetPersonID: r,
                message: `${posterName}${hasAttachments ? " sent a photo" : ""}: ${data.title} - ${data.body}`,
                title: `${data.Board?.name}`,
                parameters: {
                    boardID: data.Board?.id,
                    postID: data.id,
                    posterName,
                    postContent: `${data.title} - ${data.body}`,
                },
            }))

        if(notificationDetails.length > 0){
            createNotification(notificationDetails);
        }
    }

    if(newPost.isPublished === false){
        const scheduledStr = new Date(newPost.postedAt).toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
        Toast.delayShow({type: 'success', text2: `Your post was scheduled for ${scheduledStr}!`});
    }
    else{
        Toast.delayShow({type: 'success', text2: `Your new post was created successfully!`});
    }
    return data;
}


const updatePost = async (post) => {
    const {error, data} = await supabase.from('Posts').update(post).eq('id', post.id).select().maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updatePost", input: post, ...error}, "Something went wrong updating that post! Sometimes things are hard...")
        return {};
    }

    // No row returned — the post was deleted before the update landed.
    if(!data){
        Toast.show({type: 'error', text2: 'This post no longer exists'});
        return {};
    }

    Toast.delayShow({type: 'success', text2: `Your post was updated successfully!`});
    return data;
}

const deletePost = async ({postID}) => {
    const {error} = await supabase.from('Posts').delete().eq('id', postID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deletePost", input: postID, ...error})
        return {};
    }

    Toast.delayShow({type: 'success', text2: `This post was deleted successfully!`});

    // TODO: Remove attachments from Storage
    // response.data.deletePost.attachments?.forEach(async a => {
    //     await removeStorageFile(a);
    // })
}


const hidePost = async ({postID, hidden}) => {
    const {error, data} = await supabase.from('Posts').update({hidden: hidden}).eq('id', postID).select().single();
    
    if(error){
        ErrorHandler(Error(error.message), {mutation: "hidePost", input: {postID, hidden}, ...error}, `Something went wrong ${hidden ? 'hiding' : 'unhiding'} that post`)
        return {};
    }

    Toast.delayShow({type: 'success', text2: `This post was ${hidden ? 'hidden' : 'unhidden'} successfully!`});
    return data;
}


const togglePostPin = async ({postID, isPinned}) => {
    const {error, data} = await supabase.rpc('toggle_post_pin', {p_post_id: postID, p_is_pinned: isPinned});
    if(error){
        ErrorHandler(Error(error.message), {mutation: 'togglePostPin', input: {postID, isPinned}, ...error}, `Something went wrong ${isPinned ? 'pinning' : 'unpinning'} that post`);
        return {};
    }
    return data;
}


const saveBoard = async ({details, type, creator}) => {
    const orgID = await getCurrentOrgID();

    const newBoard = {
        id: details.id || Crypto.randomUUID(),
        organizationID: orgID,
        type: type,
        name: details.name,
        description: details.description
    }

    if(details.heroImageFile){
        const avatarPath = `${orgID}/boards/${newBoard.id}/${Crypto.randomUUID()}.jpg`;
        const uploadResult = await saveFile(details.heroImageFile, avatarPath);
        if(!uploadResult || uploadResult.error) throw Error("Board hero image upload failed");
        newBoard.avatarPath = avatarPath;
    }

    if(details.id){
        return updateBoard(newBoard, details.Roles);
    }
    else{
        if(!creator){
            Toast.show({type: 'error', text2: 'Still loading your profile — try again in a moment'});
            throw Error("Cannot create board without a creator");
        }
        return createBoard({...newBoard, createdByID: creator}, details.Roles)
    }
}


const createBoard = async (newBoard, roles = []) => {
    const {error} = await supabase.from('Boards').insert(newBoard);
    const {error:error2, data} = await supabase.from('Boards').select('*, Roles(*)').eq('organizationID', newBoard.organizationID).eq('id', newBoard.id).maybeSingle();
    let newRoles = [];

    if(error || error2){
        ErrorHandler(Error(error?.message || error2?.message), {mutation: "createBoard", input: newBoard, ...(error || error2)}, `Something went wrong creating that board`);
        throw Error();
    }

    if(roles.length > 0){
        const {error: roleError, data: roleData} = await supabase.from('BoardRoles').insert(roles?.map(r => ({organizationID: newBoard.organizationID, boardID: data.id, roleID: r.id, canPost: r.canPost, canReact: r.canReact, canComment: r.canComment, isAdmin: r.isAdmin})).flat()).select();
        newRoles = roleData

        if(roleError){
            ErrorHandler(Error(roleError.message), {mutation: "createBoard", input: roles, ...error}, `We created the Board, but something went wrong creating the roles for it`);
            throw Error();
        }
    }

    Toast.delayShow({type: 'success', text2: `New Board has been added successfully!`});
    return {...data, Roles: newRoles};
}


const updateBoard = async (newBoard, roles = []) => {
    const {error, data} = await supabase.from('Boards').update(newBoard).eq('id', newBoard.id).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateBoard", input: newBoard, ...error}, "Something went wrong updating that board! Sometimes things are hard...")
        return {};
    }

    // TODO: Change this to a transaction in a function OR compare & update like before
    const {error: deleteError} = await supabase.from("BoardRoles").delete().eq("boardID", newBoard.id);
    const {error: insertError, data: insertData} = await supabase.from("BoardRoles").insert(roles?.map(r => ({organizationID: data.organizationID, boardID: newBoard.id, roleID: r.id, canPost: r.canPost, canReact: r.canReact, canComment: r.canComment, isAdmin: r.isAdmin})).flat());

    if(deleteError || insertError){
        ErrorHandler(Error(deleteError?.message || insertError?.message), {mutation: "updateBoard", input: roles, ...error}, `We updated the Board itself, but something went wrong updating the roles for it`);
        throw Error();
    }

    
    Toast.delayShow({type: 'success', text2: `Board has been updated successfully!`});
    return {...data, Roles: insertData};
}


const deleteBoard = async ({boardID}) => {
    const {error} = await supabase.from('Boards').delete().eq('id', boardID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteBoard", input: boardID, ...error}, `Something went wrong deleting that board`)
        return {};
    }

    Toast.delayShow({type: 'success', text2: `This board was deleted successfully!`});
}


const saveComment = async ({details}) => {
    const orgID = await getCurrentOrgID();

    const new_comment = {
        postID: details.postID,
        authorID: details.authorID,
        organizationID: orgID,
        commentedAt: new Date().toISOString(),
        text: details.text,
    }

    //Only set attachments when explicitly provided — edits that don't touch
    //attachments (e.g. text-only edit via EditContainer) must preserve them.
    if(details.attachments !== undefined){
        new_comment.attachments = details.attachments
    }

    if(details.id){
        new_comment.id = details.id
        return updateComment(new_comment)
    }
    else{
        return createComment(new_comment)
    }
}


const createComment = async (new_comment) => {
    const {error, data} = await supabase.from('Comments').insert(new_comment).select('*, Post:Posts(id, title, Comments(author:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)))').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createComment", input: new_comment, ...error}, `Looks like we couldn't post that comment`);
        return {};
    }

    return data;
}

const updateComment = async (new_comment) => {
    const {error, data} = await supabase.from('Comments').update(new_comment).eq('id', new_comment.id).select('*, author:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)').single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateComment", input: new_comment, ...error})
        return {};
    }

    return data;
}

const deleteComment = async ({commentID}) => {
    const {error} = await supabase.from('Comments').delete().eq('id', commentID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteComment", input: commentID, ...error})
        return {};
    }

    Toast.delayShow({type: 'success', text2: `This comment was deleted successfully!`});
}

const getMessagePageCountInConversation = async (conversationID) => {
    const {error, count} = await supabase.from('Messages').select('*', {count: 'exact', head: true}).eq('conversationID', conversationID);

    if(error){
        ErrorHandler(Error(error.message), {query: "getMessageCountInConversation", input: conversationID, ...error}, `Something went wrong getting the message count for that conversation`);
        return 0;
    }

    const numberOfPages = Math.ceil(count / 25);
    return numberOfPages;
}

const getMessagesForConversation = async (conversationID, pageParam = 0) => {
    const {error, data} = await supabase.from('Messages').select(`*, 
        Sender:People!senderID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), 
        repliedTo:repliedToID(*, Sender:People!senderID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)),
        Reactions:MessageReactions(*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))
    `)
    .eq('conversationID', conversationID)
    .order('createdAt', {ascending: false})
    .order('sentAt', {ascending: false})
    .range(pageParam * 50, (pageParam * 50) + 49)

    if(error){
        ErrorHandler(Error(error.message), {query: "getMessagesForConversation", input: conversationID, ...error}, `Something went wrong fetching the messages for that conversation`);
        return {data: [], nextPage: undefined};
    }

    return { data: data, nextPage: data.length < 50 ? undefined : pageParam + 1, previousPage: pageParam == 0 ? undefined : pageParam - 1 };
}


const createConversation = async ({details}) => {
    const orgID = await getCurrentOrgID();
    const isRoleBased = details.membersByGroup === true;

    //Only check for existing conversation for person-based conversations
    if(!isRoleBased){
        const { data: existingConversation, error: existingError } = await supabase.rpc('find_existing_conversation', { person_ids: details.People?.map(p => p.id).sort() });

        if(existingConversation?.length > 0 && !existingError){
            return {existingID: existingConversation[0].conversation_id};
        }
    }

    const new_convo = {
        name: !details.name ? "*NONE*" : details.name,
        organizationID: orgID,
        avatarPath: details.avatarPath,
        membersByGroup: isRoleBased,
        createdByID: details.createdByID,
    }

    const {error, data} = await supabase.from('Conversations').insert(new_convo).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createConversation", input: new_convo, ...error}, `Something went wrong creating that conversation`);
        return {};
    }

    if(isRoleBased){
        //Insert RoleConversations entries
        const roleEntries = details.Roles?.map(r => ({roleID: r.id, organizationID: orgID, conversationID: data.id})) || [];
        if(roleEntries.length > 0){
            const {error: roleError} = await supabase.from('RoleConversations').insert(roleEntries);
            if(roleError){
                ErrorHandler(Error(roleError.message), {mutation: "createConversation", input: details.Roles, ...roleError}, `We created the Conversation, but something went wrong adding the roles to it`);
                //Return the conversation data anyway so the user can be navigated to it
                return {...data, Roles: []};
            }
        }
        return {...data, Roles: details.Roles};
    }
    else{
        const {error: personError, data: personData} = await supabase.from('PersonConversations').insert(details.People?.map(p => ({personID: p.id, organizationID: orgID, conversationID: data.id, isAdmin: false, isMuted: false, isPinned: false})).flat()).select();

        if(personError){
            ErrorHandler(Error(personError.message), {mutation: "createConversation", input: details.People, ...error}, `We created the Conversation, but something went wrong adding the people to it`);
            return {};
        }

        return {...data, People: personData};
    }
}


const updateConversation = async ({details}) => {
    const isRoleBased = details.membersByGroup === true;

    const updated_convo = {
        name: !details.name ? "*NONE*" : details.name,
        avatarPath: details.avatarPath,
    }

    const {error, data} = await supabase.from('Conversations').update(updated_convo).eq('id', details.id).select('*, People:People!PersonConversations(id), Roles:Roles!RoleConversations(id)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateConversation", input: updated_convo, ...error}, `Something went wrong updating that conversation`);
        return {};
    }

    if(isRoleBased){
        //Sync RoleConversations
        const currentRoleIDs = data.Roles?.map(r => r.id) || [];
        const newRoleIDs = details.Roles?.map(r => r.id) || [];
        const rolesToAdd = newRoleIDs.filter(id => !currentRoleIDs.includes(id));
        const rolesToRemove = currentRoleIDs.filter(id => !newRoleIDs.includes(id));

        if(rolesToAdd.length > 0){
            const {error: roleAddError} = await supabase.from('RoleConversations').insert(rolesToAdd.map(roleID => ({roleID, organizationID: data.organizationID, conversationID: data.id})));
            if(roleAddError){
                ErrorHandler(Error(roleAddError.message), {mutation: "updateConversation", input: {rolesToAdd}, ...roleAddError}, `We updated the Conversation, but something went wrong adding the roles`);
                return {};
            }
        }
        if(rolesToRemove.length > 0){
            const {error: roleRemoveError} = await supabase.from('RoleConversations').delete().eq('conversationID', data.id).in('roleID', rolesToRemove);
            if(roleRemoveError){
                ErrorHandler(Error(roleRemoveError.message), {mutation: "updateConversation", input: {rolesToRemove}, ...roleRemoveError}, `We updated the Conversation, but something went wrong removing the roles`);
                return {};
            }
        }
    }
    else{
        const {itemsToAdd, itemsToDelete} = findAssociativeChanges(details.People, data.People)

        const {error: personError} = await supabase.from('PersonConversations').insert(itemsToAdd?.map(p => ({personID: p.id, organizationID: data.organizationID, conversationID: data.id, isAdmin: false, isMuted: false, isPinned: false})).flat()).select();
        const {error: personDeleteError} = await supabase.from('PersonConversations').delete().eq('conversationID', data.id).in('personID', itemsToDelete?.map(p => p.id));

        if(personError || personDeleteError){
            ErrorHandler(Error(personError?.message || personDeleteError?.message), {mutation: "updateConversation", input: {itemsToAdd, itemsToDelete}, ...error}, `We updated the Conversation, but something went wrong adding or removing the people from it`);
            return {};
        }
    }

    return data;

}


const deleteConversation = async ({conversationID, personID}) => {
    const {error} = await supabase.from('PersonConversations').update({isDeleted: true}).eq('conversationID', conversationID).eq('personID', personID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteConversation", input: {conversationID, personID}, ...error}, `Something went wrong deleting that conversation`);
        return {};
    }

    Toast.delayShow({type: 'success', text2: `This conversation has been removed successfully`});
}


const toggleConversationPin = async ({conversationID, personID, isPinned}) => {
    const {error, data} = await supabase.from('PersonConversations').update({isPinned: isPinned}).eq('conversationID', conversationID).eq('personID', personID).select('personID, conversationID, isPinned').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "toggleConversationPin", input: conversationID, ...error});
    }   

    return data;
}


const toggleConversationMute = async ({conversationID, personID, isMuted}) => {
    const {error, data} = await supabase.from('PersonConversations').update({isMuted: isMuted}).eq('conversationID', conversationID).eq('personID', personID).select('personID, conversationID, isMuted').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "toggleConversationMute", input: conversationID, ...error});
    }

    return data;
}


const toggleBoardMute = async ({boardID, personID, isMuted}) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('PersonBoards')
        .upsert({boardID, personID, organizationID: orgID, isMuted}, {onConflict: 'boardID,personID'})
        .select('boardID, personID, isMuted')
        .maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "toggleBoardMute", input: {boardID, personID, isMuted}, ...error});
    }

    return data;
}


const toggleBoardPin = async ({boardID, personID, isPinned}) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('PersonBoards')
        .upsert({boardID, personID, organizationID: orgID, isPinned}, {onConflict: 'boardID,personID'})
        .select('boardID, personID, isPinned')
        .maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "toggleBoardPin", input: {boardID, personID, isPinned}, ...error});
    }

    return data;
}


//Hiding a board also mutes it (single upsert); unhiding unmutes by default.
const toggleBoardHide = async ({boardID, personID, isHidden}) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('PersonBoards')
        .upsert({boardID, personID, organizationID: orgID, isHidden, isMuted: isHidden}, {onConflict: 'boardID,personID'})
        .select('boardID, personID, isHidden, isMuted')
        .maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "toggleBoardHide", input: {boardID, personID, isHidden}, ...error});
    }

    return data;
}


//Board-admin/org-admin only (RLS enforced server-side). Archives are org-wide.
const setBoardArchived = async ({boardID, isArchived}) => {
    const {error, data} = await supabase.rpc('set_board_archived', {p_board_id: boardID, p_is_archived: isArchived});

    if(error){
        ErrorHandler(Error(error.message), {mutation: "setBoardArchived", input: {boardID, isArchived}, ...error}, `Something went wrong ${isArchived ? 'archiving' : 'unarchiving'} that board`);
        return {};
    }

    Toast.delayShow({type: 'success', text2: `This board was ${isArchived ? 'archived' : 'unarchived'} successfully!`});
    return data;
}


const getMyBoardStates = async (boardIDs) => {
    const empty = {muteMap: new Map(), pinMap: new Map(), hideMap: new Map()};
    if(!boardIDs || boardIDs.length === 0){
        return empty;
    }

    const loggedInUser = queryClient.getQueryData(QueryKeys.people.loggedInUser());
    if(!loggedInUser?.id){
        return empty;
    }

    const {error, data} = await supabase.from('PersonBoards')
        .select('boardID, isMuted, isPinned, isHidden')
        .in('boardID', boardIDs)
        .eq('personID', loggedInUser.id);

    if(error){
        ErrorHandler(Error(error.message), {query: "getMyBoardStates", input: {boardIDs, personID: loggedInUser.id}, ...error});
        return empty;
    }

    const muteMap = new Map(), pinMap = new Map(), hideMap = new Map();
    (data || []).forEach(row => {
        muteMap.set(row.boardID, row.isMuted ?? false);
        pinMap.set(row.boardID, row.isPinned ?? false);
        hideMap.set(row.boardID, row.isHidden ?? false);
    });
    return {muteMap, pinMap, hideMap};
}


const getMutedPersonIDsForBoard = async (boardID) => {
    const {error, data} = await supabase.from('PersonBoards')
        .select('personID')
        .eq('boardID', boardID)
        .eq('isMuted', true);

    if(error){
        ErrorHandler(Error(error.message), {query: "getMutedPersonIDsForBoard", input: {boardID}, ...error});
        return [];
    }

    return (data || []).map(row => row.personID);
}


//Resolve all people who can access a board: everyone in any role granted
//access via BoardRoles (RolePeople → People), plus the board creator.
//Deduplicates by person ID (a person may be in multiple roles).
const getBoardMembers = async (boardID) => {
    const orgID = await getCurrentOrgID();

    const {error: boardError, data: board} = await supabase.from('Boards')
        .select('*, Roles:BoardRoles(*, Role:Roles(*))')
        .eq('organizationID', orgID)
        .eq('id', boardID)
        .maybeSingle();

    if(boardError){
        ErrorHandler(Error(boardError.message), {query: "getBoardMembers", input: {boardID}, ...boardError});
        return [];
    }
    if(!board) return [];

    if(board.type === 'ANNOUNCEMENTS'){
        const members = [];
        for(let offset = 0; ; offset += 500){
            const {data, error} = await supabase.from('RegisteredUsersOrganizations').select('Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)').eq('organizationID', orgID).order('personID').range(offset, offset + 499);
            if(error) throw new Error(error.message);
            members.push(...data.map(member => member.Person).filter(Boolean));
            if(data.length < 500) break;
        }
        return [...new Map(members.map(person => [person.id, person])).values()];
    }
    const roleIDs = board.Roles?.map(r => r.roleID) || [];

    let people = [];
    if(roleIDs.length > 0){
        const {error: rpError, data: rolePeople} = await supabase.from('RolePeople')
            .select('Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), roleID')
            .in('roleID', roleIDs)
            .eq('organizationID', orgID);

        if(rpError){
            ErrorHandler(Error(rpError.message), {query: "getBoardMembers (role people)", input: {boardID, roleIDs}, ...rpError});
        }
        else if(rolePeople){
            people = rolePeople.map(rp => rp.Person).filter(Boolean);
        }
    }

    //Add the board creator (they always have access even without a role entry)
    if(board.createdByID){
        const {data: creator} = await supabase.from('People')
            .select('id, firstName, lastName, avatarUpdatedAt, avatarStorageKey')
            .eq('id', board.createdByID)
            .maybeSingle();
        if(creator) people.unshift(creator);
    }

    //Deduplicate
    const seen = new Set();
    return people.filter(p => {
        if(!p || seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
    });
}


const createMessage = async ({details}) => {
    const orgID = await getCurrentOrgID();

    const new_message = {
        id: details.id,
        senderID: details.senderID,
        organizationID: orgID,
        conversationID: details.conversationID,
        sentAt: new Date().toISOString(),
        text: details.text,
        attachments: [...new Set(details.attachments)],
        repliedToID:details.repliedToID
    }

    const {error, data} = await supabase.from('Messages').insert(new_message).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createMessage", input: new_message, ...error}, `Looks like we couldn't send that message`);
        throw Error(error.message); //Throw so mutation onError can mark the optimistic message as failed
    }

    return data;
}


const updateMessage = async ({details}) => {

    const new_message = {
        id: details.id,
        text: details.text,
        attachments: [...new Set(details.attachments)],
        repliedToID:details.repliedToID,
        isDeleted: details.isDeleted
    }

    const {error, data} = await supabase.from('Messages').update(new_message).eq('id', details.id).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateMessage", input: new_message, ...error}, `Looks like we couldn't update that message`);
        return {};
    }

    return data;
}

const addMessageReaction = async ({messageID, personID, reaction}) => {
    const orgID = await getCurrentOrgID();
    // Upsert on (personID, messageID): a person can only have one reaction per
    // message (unique_person_message index). Upsert handles the "switch reaction"
    // case cleanly without racing a separate delete call.
    const {error, data} = await supabase.from('MessageReactions').upsert({messageID: messageID, personID: personID, reaction: reaction, organizationID: orgID}, {onConflict: 'personID,messageID'}).select('*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "addMessageReaction", input: {messageID, personID, reaction}, ...error});
        throw Error();
    }

    return data;
}

const removeMessageReaction = async ({messageID, personID}) => {
    const orgID = await getCurrentOrgID();
    const {error, data} = await supabase.from('MessageReactions').delete().eq('messageID', messageID).eq('personID', personID).eq('organizationID', orgID).select('*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "removeMessageReaction", input: {messageID, personID}, ...error});
        return {};
    }   
    return data;
}

const addPostReaction = async ({postID, personID, reaction}) => {
    const orgID = await getCurrentOrgID();
    const {error, data} = await supabase.from('PostReactions').upsert({postID: postID, personID: personID, reaction: reaction, organizationID: orgID}, {onConflict: 'postID,personID'}).select('*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "addPostReaction", input: {postID, personID, reaction}, ...error});
        throw Error();
    }   
    return data;
}

const removePostReaction = async ({postID, personID}) => {
    const orgID = await getCurrentOrgID();
    const {error, data} = await supabase.from('PostReactions').delete().eq('postID', postID).eq('personID', personID).eq('organizationID', orgID).select('*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "removePostReaction", input: {postID, personID}, ...error});
        return {};
    }   
    return data;
}

const addCommentReaction = async ({commentID, personID, reaction}) => {
    const orgID = await getCurrentOrgID();
    // Upsert on (personID, commentID): a person can only have one reaction per
    // comment. Upsert handles the "switch reaction" case cleanly without racing
    // a separate delete call.
    const {error, data} = await supabase.from('CommentReactions').upsert({commentID: commentID, personID: personID, reaction: reaction, organizationID: orgID}, {onConflict: 'personID,commentID'}).select('*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "addCommentReaction", input: {commentID, personID, reaction}, ...error});
        throw Error();
    }   
    return data;
}

const removeCommentReaction = async ({commentID, personID}) => {
    const orgID = await getCurrentOrgID();
    const {error, data} = await supabase.from('CommentReactions').delete().eq('commentID', commentID).eq('personID', personID).eq('organizationID', orgID).select('*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "removeCommentReaction", input: {commentID, personID}, ...error});
        return {};
    }   
    return data;
}


const getGifs = async ({searchTerm, pageParam}) => {
    const {data, error} = await supabase.functions.invoke('get-gifs', {
        body: {
            searchTerm: searchTerm,
            nextPageKey: pageParam
        }
    });

    if(error){
        ErrorHandler(Error(error.message), {query: "getGifs", searchTerm, pageParam, ...error}, `Something went wrong fetching the GIFs`)
        return {next: undefined, results: []};
    }

    return {results: data.data?.results, next: data.data?.next};
}


const getMessageById = async (id) => {
    const {data, error} = await supabase.from('Messages')
        .select('*, Sender:People!senderID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), repliedTo:repliedToID(*, Sender:People!senderID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), Reactions:MessageReactions(*, Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))')
        .eq('id', id)
        .maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getMessageById", input: {id}, ...error});
        return null;
    }
    return data;
}

const getCommentById = async (id) => {
    const {data, error} = await supabase.from('Comments')
        .select('*, author:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
        .eq('id', id)
        .maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getCommentById", input: {id}, ...error});
        return null;
    }
    return data;
}


export {
    getGifs,
    getConversations,
    getConversation,
    getBoards,
    getBoardPermissions,
    getMyBoardsFeed,
    getPostsForBoard,
    getCommentsForPost,
    saveBoard,
    deleteBoard,
    savePost,
    hidePost,
    deletePost,
    saveComment,
    deleteComment,
    getBoard,
    getBoardMembers,
    getPost,
    createConversation,
    updateConversation,
    deleteConversation,
    toggleConversationPin,
    toggleConversationMute,
    togglePostPin,
    toggleBoardMute,
    toggleBoardPin,
    toggleBoardHide,
    setBoardArchived,
    getMyBoardStates,
    getMutedPersonIDsForBoard,
    createMessage,
    getMessagesForConversation,
    updateMessage,
    addMessageReaction,
    removeMessageReaction,
    addPostReaction,
    removePostReaction,
    addCommentReaction,
    removeCommentReaction,
    getMessagePageCountInConversation,
    getAllOrgConversations,
    getAllOrgBoards,
    getMessageById,
    getCommentById,
    recordPostView,
    getPostViewers
}